import { confirmSignIn, fetchAuthSession, signIn } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import { useCallback, useState } from 'react'

import PasskeyMetadataService from '../services/passkey-metadata'

export interface PasskeyAuthStatus {
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
}

export function usePasskeyAuth() {
  const [status, setStatus] = useState<PasskeyAuthStatus | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handlePasskeySignIn = useCallback(async (email: string): Promise<boolean> => {
    if (!email) {
      setStatus({ message: 'Please enter your email first', type: 'warning' })
      return false
    }

    setIsLoading(true)

    try {
      setStatus({ message: 'Checking for passkeys...', type: 'info' })

      let result = await signIn({
        username: email,
        options: {
          authFlowType: 'USER_AUTH' as const,
          preferredChallenge: 'WEB_AUTHN' as const
        }
      })

      if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
        if (result.nextStep.availableChallenges?.includes('WEB_AUTHN')) {
          setStatus({ message: 'Authenticating with passkey...', type: 'info' })

          result = await confirmSignIn({
            challengeResponse: 'WEB_AUTHN'
          })
        } else {
          setStatus({ message: 'No passkey found. Please use your password.', type: 'warning' })
          setTimeout(() => setStatus(null), 3000)
          setIsLoading(false)
          return false
        }
      }

      if (result.isSignedIn) {
        setStatus({ message: 'Success! Redirecting...', type: 'success' })

        try {
          const metadataList = await PasskeyMetadataService.getAll()
          if (metadataList.length > 0) {
            const mostRecent = metadataList
              .slice()
              .sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
              )[0]
            if (mostRecent) {
              await PasskeyMetadataService.updateLastUsed(mostRecent.credential_id)
            }
          }
        } catch (metadataError) {
          console.error('Failed to update passkey metadata after sign-in', metadataError)
        }

        await fetchAuthSession()
        Hub.dispatch('auth', { event: 'signIn', data: result })

        setTimeout(() => window.location.reload(), 500)
        setIsLoading(false)
        return true
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error('Passkey sign-in error:', error)

        if (error.message?.includes('User does not exist')) {
          setStatus({ message: 'No account found with this email', type: 'error' })
        } else if (error.message?.includes('No passkey')) {
          setStatus({
            message: 'No passkey registered. Please use your password.',
            type: 'warning'
          })
        } else {
          setStatus({ message: 'Passkey authentication failed. Try password.', type: 'error' })
        }

        setTimeout(() => setStatus(null), 4000)
        setIsLoading(false)
        return false
      }
    }

    setIsLoading(false)
    return false
  }, [])

  const clearStatus = useCallback(() => {
    setStatus(null)
  }, [])

  return {
    handlePasskeySignIn,
    status,
    isLoading,
    clearStatus
  }
}
